// src/components/templates/diary/DiaryPlannerView.jsx
import React, { useState, useMemo } from 'react';
import { 
  Calendar, Clock, Plus, Filter, ChevronLeft, ChevronRight, CheckCircle2, Circle,
  Bell, BellRing, CalendarCheck, CalendarClock, Users, MapPin, Video, Building2,
  Flag, AlertCircle, MoreVertical, Edit2, Trash2, X, Save, Check, ExternalLink,
  ListTodo, CalendarDays, Timer, UserCheck, UserX, Mail, Send, Copy, Star,
  Briefcase, User, ArrowRight, ChevronDown, Search, SlidersHorizontal, RefreshCw,
  ClipboardList, Ticket, CalendarPlus, BellPlus, Eye, EyeOff, Repeat, Link
} from 'lucide-react';
import { Button, Card, Badge, Input, TextArea, Select, Modal } from './UIComponents';

// --- CONSTANTS ---
const ItemType = {
  TASK: 'TASK',
  EVENT_HOSTING: 'EVENT_HOSTING',
  EVENT_ATTENDING: 'EVENT_ATTENDING',
  BOOKING_PROVIDER: 'BOOKING_PROVIDER',
  BOOKING_CUSTOMER: 'BOOKING_CUSTOMER',
  GENERAL: 'GENERAL'
};

const TaskPriority = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  URGENT: 'URGENT'
};

const TaskStatus = {
  TODO: 'TODO',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED'
};

const EventStatus = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  CANCELLED: 'CANCELLED',
  COMPLETED: 'COMPLETED'
};

const BookingStatus = {
  PENDING: 'PENDING',
  CONFIRMED: 'CONFIRMED',
  CANCELLED: 'CANCELLED',
  COMPLETED: 'COMPLETED',
  NO_SHOW: 'NO_SHOW'
};

const ReminderTiming = {
  AT_TIME: 'AT_TIME',
  MINUTES_5: 'MINUTES_5',
  MINUTES_15: 'MINUTES_15',
  MINUTES_30: 'MINUTES_30',
  HOURS_1: 'HOURS_1',
  HOURS_24: 'HOURS_24',
  DAYS_1: 'DAYS_1',
  DAYS_7: 'DAYS_7'
};

// --- HELPER FUNCTIONS ---
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${m} ${ampm}`;
};

const isToday = (dateStr) => {
  return dateStr === new Date().toISOString().split('T')[0];
};

const isTomorrow = (dateStr) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return dateStr === tomorrow.toISOString().split('T')[0];
};

const isOverdue = (dateStr, timeStr) => {
  const now = new Date();
  const itemDate = new Date(`${dateStr}T${timeStr || '23:59'}`);
  return itemDate < now;
};

const getDaysUntil = (dateStr) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
};

// --- PRIORITY BADGE ---
const PriorityBadge = ({ priority }) => {
  const config = {
    [TaskPriority.LOW]: { color: 'gray', icon: Flag },
    [TaskPriority.MEDIUM]: { color: 'blue', icon: Flag },
    [TaskPriority.HIGH]: { color: 'amber', icon: Flag },
    [TaskPriority.URGENT]: { color: 'red', icon: AlertCircle }
  };
  const { color, icon: Icon } = config[priority] || config[TaskPriority.MEDIUM];
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-700`}>
      <Icon className="w-3 h-3" />
      {priority}
    </span>
  );
};

// --- TYPE ICON ---
const TypeIcon = ({ type, className = "w-5 h-5" }) => {
  const icons = {
    [ItemType.TASK]: ListTodo,
    [ItemType.EVENT_HOSTING]: CalendarCheck,
    [ItemType.EVENT_ATTENDING]: Ticket,
    [ItemType.BOOKING_PROVIDER]: Briefcase,
    [ItemType.BOOKING_CUSTOMER]: CalendarClock
  };
  const Icon = icons[type] || ListTodo;
  return <Icon className={className} />;
};

// --- REMINDER INDICATOR ---
const ReminderIndicator = ({ reminders = [] }) => {
  if (!reminders.length) return null;
  
  const activeReminders = reminders.filter(r => !r.dismissed);
  if (!activeReminders.length) return null;

  return (
    <div className="flex items-center gap-1 text-amber-600">
      <BellRing className="w-4 h-4" />
      <span className="text-xs font-medium">{activeReminders.length}</span>
    </div>
  );
};

// --- RSVP STATUS ---
const RSVPSummary = ({ rsvps = [] }) => {
  const yes = rsvps.filter(r => r.status === 'YES').length;
  const no = rsvps.filter(r => r.status === 'NO').length;
  const maybe = rsvps.filter(r => r.status === 'MAYBE').length;
  const pending = rsvps.filter(r => r.status === 'PENDING').length;

  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="flex items-center gap-1 text-green-600">
        <UserCheck className="w-3.5 h-3.5" /> {yes}
      </span>
      <span className="flex items-center gap-1 text-red-600">
        <UserX className="w-3.5 h-3.5" /> {no}
      </span>
      {maybe > 0 && (
        <span className="flex items-center gap-1 text-amber-600">
          <User className="w-3.5 h-3.5" /> {maybe}
        </span>
      )}
      {pending > 0 && (
        <span className="flex items-center gap-1 text-gray-500">
          <Clock className="w-3.5 h-3.5" /> {pending}
        </span>
      )}
    </div>
  );
};

// --- QUICK DATE BADGE ---
const DateBadge = ({ date }) => {
  const daysUntil = getDaysUntil(date);
  
  if (daysUntil < 0) {
    return <Badge color="red">Overdue</Badge>;
  }
  if (daysUntil === 0) {
    return <Badge color="amber">Today</Badge>;
  }
  if (daysUntil === 1) {
    return <Badge color="blue">Tomorrow</Badge>;
  }
  if (daysUntil <= 7) {
    return <Badge color="gray">In {daysUntil} days</Badge>;
  }
  return null;
};

// --- PLANNER ITEM CARD ---
const PlannerItemCard = ({ item, onEdit, onDelete, onToggleComplete, onViewDetails }) => {
  const [showMenu, setShowMenu] = useState(false);

  const getTypeConfig = (type) => {
    const configs = {
      [ItemType.TASK]: { 
        color: 'indigo', 
        label: 'Task',
        gradient: 'from-indigo-500 to-indigo-600'
      },
      [ItemType.EVENT_HOSTING]: { 
        color: 'purple', 
        label: 'Hosting Event',
        gradient: 'from-purple-500 to-purple-600'
      },
      [ItemType.EVENT_ATTENDING]: { 
        color: 'pink', 
        label: 'Attending',
        gradient: 'from-pink-500 to-pink-600'
      },
      [ItemType.GENERAL]: {
        color: 'gray',
        label: 'General',
        gradient: 'from-gray-400 to-gray-600'
      },

      [ItemType.BOOKING_PROVIDER]: { 
        color: 'green', 
        label: 'Client Booking',
        gradient: 'from-green-500 to-green-600'
      },
      [ItemType.BOOKING_CUSTOMER]: { 
        color: 'teal', 
        label: 'My Appointment',
        gradient: 'from-teal-500 to-teal-600'
      }
    };
    return configs[type] || configs[ItemType.TASK];
  };

  const config = getTypeConfig(item.type);
  const isCompleted = item.status === TaskStatus.COMPLETED || 
                      item.status === BookingStatus.COMPLETED ||
                      item.status === EventStatus.COMPLETED;

  return (
    <Card className={`overflow-hidden hover:shadow-lg transition-all group ${isCompleted ? 'opacity-60' : ''}`}>
      {/* Type indicator strip */}
      <div className={`h-1 bg-gradient-to-r ${config.gradient}`} />
      
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg bg-${config.color}-100`}>
              <TypeIcon type={item.type} className={`w-4 h-4 text-${config.color}-600`} />
            </div>
            <div>
              <span className={`text-xs font-medium text-${config.color}-600`}>{config.label}</span>
              <DateBadge date={item.date} />
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <ReminderIndicator reminders={item.reminders} />
            
            <div className="relative">
              <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-8 z-20 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-1">
                    <button 
                      onClick={() => { onViewDetails?.(item); setShowMenu(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" /> View Details
                    </button>
                    <button 
                      onClick={() => { onEdit?.(item); setShowMenu(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" /> Edit
                    </button>
                    <button 
                      onClick={() => { onDelete?.(item.id); setShowMenu(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Title & Description */}
        <div className="flex items-start gap-3 mb-3">
          {item.type === ItemType.TASK && (
            <button 
              onClick={() => onToggleComplete?.(item)}
              className="mt-0.5 shrink-0"
            >
              {isCompleted ? (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              ) : (
                <Circle className="w-5 h-5 text-gray-300 hover:text-indigo-500" />
              )}
            </button>
          )}
          <div className="flex-1 min-w-0">
            <h3 className={`font-semibold text-gray-900 ${isCompleted ? 'line-through text-gray-500' : ''}`}>
              {item.title}
            </h3>
            {item.description && (
              <p className="text-sm text-gray-500 line-clamp-2 mt-1">{item.description}</p>
            )}
          </div>
        </div>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {formatDate(item.date)}
            {item.isDateRange && item.endDate && (
              <> - {formatDate(item.endDate)}</>
            )}
          </span>
          {item.startTime && (
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatTime(item.startTime)}
              {item.endTime && <> - {formatTime(item.endTime)}</>}
            </span>
          )}
          {item.location && (
            <span className="flex items-center gap-1 truncate max-w-[150px]">
              <MapPin className="w-4 h-4" />
              {item.location}
            </span>
          )}
          {item.isVirtual && (
            <span className="flex items-center gap-1 text-indigo-600">
              <Video className="w-4 h-4" />
              Virtual
            </span>
          )}
        </div>

        {/* Type-specific info */}
        {item.type === ItemType.TASK && item.priority && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <PriorityBadge priority={item.priority} />
          </div>
        )}

        {item.type === ItemType.EVENT_HOSTING && item.rsvps?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <RSVPSummary rsvps={item.rsvps} />
          </div>
        )}

        {(item.type === ItemType.BOOKING_PROVIDER || item.type === ItemType.BOOKING_CUSTOMER) && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                {item.type === ItemType.BOOKING_PROVIDER ? item.customerName : item.providerName}
              </span>
            </div>
            <Badge color={
              item.status === BookingStatus.CONFIRMED ? 'green' :
              item.status === BookingStatus.PENDING ? 'amber' :
              item.status === BookingStatus.CANCELLED ? 'red' : 'gray'
            }>
              {item.status}
            </Badge>
          </div>
        )}

        {item.type === ItemType.EVENT_ATTENDING && item.myRsvp && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
            <span className="text-sm text-gray-500">My RSVP:</span>
            <Badge color={
              item.myRsvp === 'YES' ? 'green' :
              item.myRsvp === 'NO' ? 'red' :
              item.myRsvp === 'MAYBE' ? 'amber' : 'gray'
            }>
              {item.myRsvp}
            </Badge>
          </div>
        )}
      </div>
    </Card>
  );
};

// --- QUICK ADD MENU ---
const QuickAddMenu = ({ onAdd, customStyling }) => {
  const [isOpen, setIsOpen] = useState(false);

  const options = [
    { type: ItemType.TASK, label: 'Task', icon: ListTodo, color: 'indigo' },
    { type: ItemType.EVENT_HOSTING, label: 'Host Event', icon: CalendarPlus, color: 'purple' },
    { type: ItemType.EVENT_ATTENDING, label: 'Event to Attend', icon: Ticket, color: 'pink' },
    { type: ItemType.BOOKING_PROVIDER, label: 'Client Booking', icon: Briefcase, color: 'green' },
    { type: ItemType.BOOKING_CUSTOMER, label: 'Book Service', icon: CalendarClock, color: 'teal' },
  ];

  return (
    <div className="relative">
      <Button onClick={() => setIsOpen(!isOpen)} className="shadow-lg shadow-indigo-200"
        style={{ backgroundColor: customStyling.primaryColor }}
        >
        <Plus className="w-4 h-4 mr-2" />
        Add New
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-12 z-50 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2">
            {options.map(opt => (
              <button
                key={opt.type}
                onClick={() => { onAdd(opt.type); setIsOpen(false); }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors"
              >
                <div className={`p-2 rounded-lg bg-${opt.color}-100`}>
                  <opt.icon className={`w-4 h-4 text-${opt.color}-600`} />
                </div>
                <span className="font-medium text-gray-700">{opt.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// --- REMINDER MODAL ---
const ReminderModal = ({ isOpen, onClose, onSave, existingReminders = [] }) => {
  const [reminders, setReminders] = useState(existingReminders);
  const [newTiming, setNewTiming] = useState(ReminderTiming.HOURS_1);

  const timingOptions = [
    { value: ReminderTiming.AT_TIME, label: 'At time of event' },
    { value: ReminderTiming.MINUTES_5, label: '5 minutes before' },
    { value: ReminderTiming.MINUTES_15, label: '15 minutes before' },
    { value: ReminderTiming.MINUTES_30, label: '30 minutes before' },
    { value: ReminderTiming.HOURS_1, label: '1 hour before' },
    { value: ReminderTiming.HOURS_24, label: '1 day before' },
    { value: ReminderTiming.DAYS_7, label: '1 week before' },
  ];

  const addReminder = () => {
    setReminders([...reminders, { 
      id: Date.now().toString(), 
      timing: newTiming, 
      dismissed: false 
    }]);
  };

  const removeReminder = (id) => {
    setReminders(reminders.filter(r => r.id !== id));
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Set Reminders" size="md">
      <div className="space-y-4">
        <div className="flex gap-2">
          <select
            value={newTiming}
            onChange={(e) => setNewTiming(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            {timingOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <Button onClick={addReminder}>
            <BellPlus className="w-4 h-4 mr-2" /> Add
          </Button>
        </div>

        <div className="space-y-2 max-h-60 overflow-y-auto">
          {reminders.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No reminders set</p>
          ) : (
            reminders.map(reminder => (
              <div key={reminder.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-amber-500" />
                  <span className="text-sm">
                    {timingOptions.find(o => o.value === reminder.timing)?.label}
                  </span>
                </div>
                <button onClick={() => removeReminder(reminder.id)} className="text-gray-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { onSave(reminders); onClose(); }}>Save Reminders</Button>
        </div>
      </div>
    </Modal>
  );
};

// --- ITEM FORM MODAL ---
const ItemFormModal = ({ isOpen, onClose, onSave, item, type }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    isDateRange: false,
    date: new Date().toISOString().split('T')[0],
    endDate: '',
    startTime: '09:00',
    endTime: '10:00',
    location: '',
    isVirtual: false,
    meetingLink: '',
    priority: TaskPriority.MEDIUM,
    status: TaskStatus.TODO,
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    providerName: '',
    serviceName: '',
    notes: '',
    reminders: [],
    recurrence: 'NONE',
    type: type || item?.type || ItemType.GENERAL,
    ...item
  });
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [customType, setCustomType] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!formData.title.trim()) return;
    onSave({
      ...formData,
      id: item?.id || Date.now().toString(),
      type: type || item?.type,
      createdAt: item?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    onClose();
  };

  const isTask = type === ItemType.TASK || item?.type === ItemType.TASK;
  const isEventHosting = type === ItemType.EVENT_HOSTING || item?.type === ItemType.EVENT_HOSTING;
  const isEventAttending = type === ItemType.EVENT_ATTENDING || item?.type === ItemType.EVENT_ATTENDING;
  const isBookingProvider = type === ItemType.BOOKING_PROVIDER || item?.type === ItemType.BOOKING_PROVIDER;
  const isBookingCustomer = type === ItemType.BOOKING_CUSTOMER || item?.type === ItemType.BOOKING_CUSTOMER;

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={item ? 'Edit Item' : 'Create New Item'} size="lg">
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder={isTask ? "What needs to be done?" : "Event or appointment name"}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Add details..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium justify-start text-gray-700 mb-1">
            Type
          </label>

          <div className="flex gap-2">
            {/* Dropdown of standard types */}
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
            >
              <option value={ItemType.TASK}>Task</option>
              <option value={ItemType.EVENT_HOSTING}>Hosting</option>
              <option value={ItemType.EVENT_ATTENDING}>Attending</option>
              <option value={ItemType.BOOKING_PROVIDER}>Client Booking</option>
              <option value={ItemType.BOOKING_CUSTOMER}>My Appointment</option>

              {/* NEW */}
              <option value={ItemType.GENERAL}>General</option>
            </select>

            {/* Free-typed custom label */}
            <input
              type="text"
              placeholder="Custom type"
              value={customType}
              onChange={(e) => {
                setCustomType(e.target.value);
                setFormData({ ...formData, type: e.target.value.toUpperCase() });
              }}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Date Type Toggle */}
        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="dateType"
              checked={!formData.isDateRange}
              onChange={() => handleChange('isDateRange', false)}
              className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Single Date</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="dateType"
              checked={formData.isDateRange}
              onChange={() => handleChange('isDateRange', true)}
              className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Date Range</span>
          </label>
        </div>

        {/* Single Date with Start/End Time */}
        {!formData.isDateRange && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Date *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Start Time</label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => handleChange('startTime', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">End Time</label>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => handleChange('endTime', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Date Range with Start/End Times */}
        {formData.isDateRange && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Start Date *</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Start Time</label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => handleChange('startTime', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">End Date *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  min={formData.date}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">End Time</label>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => handleChange('endTime', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Location / Virtual */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isVirtual}
              onChange={(e) => handleChange('isVirtual', e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Virtual / Online</span>
          </label>
          
          {formData.isVirtual ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Meeting Link</label>
              <input
                type="text"
                value={formData.meetingLink}
                onChange={(e) => handleChange('meetingLink', e.target.value)}
                placeholder="https://zoom.us/j/..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Address or location name"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}
        </div>

        {/* Task-specific: Priority */}
        {isTask && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => handleChange('priority', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value={TaskPriority.LOW}>🟢 Low</option>
              <option value={TaskPriority.MEDIUM}>🔵 Medium</option>
              <option value={TaskPriority.HIGH}>🟠 High</option>
              <option value={TaskPriority.URGENT}>🔴 Urgent</option>
            </select>
          </div>
        )}

        {/* Booking Provider: Customer Info */}
        {isBookingProvider && (
          <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-700 text-left">Customer Information</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Customer Name *</label>
              <input
                type="text"
                value={formData.customerName}
                onChange={(e) => handleChange('customerName', e.target.value)}
                placeholder="Client name"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Email</label>
                <input
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => handleChange('customerEmail', e.target.value)}
                  placeholder="client@email.com"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Phone</label>
                <input
                  type="text"
                  value={formData.customerPhone}
                  onChange={(e) => handleChange('customerPhone', e.target.value)}
                  placeholder="+1 234 567 8900"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Service</label>
              <input
                type="text"
                value={formData.serviceName}
                onChange={(e) => handleChange('serviceName', e.target.value)}
                placeholder="Service being booked"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        )}

        {/* Booking Customer: Provider Info */}
        {isBookingCustomer && (
          <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-700 text-left">Appointment Details</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Provider / Business Name *</label>
              <input
                type="text"
                value={formData.providerName}
                onChange={(e) => handleChange('providerName', e.target.value)}
                placeholder="Doctor, salon, etc."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Service / Reason</label>
              <input
                type="text"
                value={formData.serviceName}
                onChange={(e) => handleChange('serviceName', e.target.value)}
                placeholder="Checkup, haircut, consultation..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        )}

        {/* Reminders */}
        <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              {formData.reminders.length} reminder{formData.reminders.length !== 1 ? 's' : ''} set
            </span>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setShowReminderModal(true)}>
            <BellPlus className="w-4 h-4 mr-1" /> Manage
          </Button>
        </div>

        {/* Recurrence */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Repeat</label>
          <select
            value={formData.recurrence}
            onChange={(e) => handleChange('recurrence', e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="NONE">Does not repeat</option>
            <option value="DAILY">Daily</option>
            <option value="WEEKLY">Weekly</option>
            <option value="MONTHLY">Monthly</option>
            <option value="YEARLY">Yearly</option>
          </select>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Additional Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Any extra notes..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!formData.title.trim()}>
          <Save className="w-4 h-4 mr-2" /> {item ? 'Update' : 'Create'}
        </Button>
      </div>

      <ReminderModal
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        existingReminders={formData.reminders}
        onSave={(reminders) => handleChange('reminders', reminders)}
      />
    </Modal>
  );
};

// --- TIMELINE VIEW ---
const TimelineView = ({ items, onEdit, onDelete, onToggleComplete, onViewDetails }) => {
  const groupedByDate = useMemo(() => {
    const groups = {};
    items.forEach(item => {
      if (!groups[item.date]) {
        groups[item.date] = [];
      }
      groups[item.date].push(item);
    });
    
    // Sort items within each date by time
    Object.keys(groups).forEach(date => {
      groups[date].sort((a, b) => (a.startTime || '00:00').localeCompare(b.startTime || '00:00'));
    });
    
    return groups;
  }, [items]);

  const sortedDates = Object.keys(groupedByDate).sort();

  return (
    <div className="space-y-6">
      {sortedDates.map(date => (
        <div key={date} className="relative">
          {/* Date header */}
          <div className="sticky top-0 z-10 bg-gray-50 py-2 px-4 rounded-lg mb-3 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
              isToday(date) ? 'bg-indigo-600 text-white' :
              isTomorrow(date) ? 'bg-indigo-100 text-indigo-600' :
              'bg-gray-200 text-gray-600'
            }`}>
              {new Date(date).getDate()}
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                {isToday(date) ? 'Today' : isTomorrow(date) ? 'Tomorrow' : formatDate(date)}
              </div>
              <div className="text-xs text-gray-500">
                {groupedByDate[date].length} item{groupedByDate[date].length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Items for this date */}
          <div className="space-y-3 pl-4 border-l-2 border-gray-200 ml-5">
            {groupedByDate[date].map(item => (
              <div key={item.id} className="relative">
                <div className="absolute -left-[25px] top-4 w-3 h-3 rounded-full bg-white border-2 border-gray-300" />
                <PlannerItemCard
                  item={item}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onToggleComplete={onToggleComplete}
                  onViewDetails={onViewDetails}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {sortedDates.length === 0 && (
        <div className="text-center py-12">
          <CalendarDays className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600">No items scheduled</h3>
          <p className="text-gray-500">Add tasks, events or bookings to see them here</p>
        </div>
      )}
    </div>
  );
};

// --- STATS BAR ---
const StatsBar = ({ items }) => {
  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const tasks = items.filter(i => i.type === ItemType.TASK);
    const events = items.filter(i => i.type === ItemType.EVENT_HOSTING || i.type === ItemType.EVENT_ATTENDING);
    const bookings = items.filter(i => i.type === ItemType.BOOKING_PROVIDER || i.type === ItemType.BOOKING_CUSTOMER);
    
    return {
      total: items.length,
      today: items.filter(i => i.date === today).length,
      overdue: items.filter(i => i.date < today && i.status !== TaskStatus.COMPLETED && i.status !== BookingStatus.COMPLETED).length,
      tasks: tasks.length,
      tasksPending: tasks.filter(t => t.status === TaskStatus.TODO || t.status === TaskStatus.IN_PROGRESS).length,
      events: events.length,
      bookings: bookings.length,
      withReminders: items.filter(i => i.reminders?.length > 0).length
    };
  }, [items]);

  return (
    <div className="flex flex-wrap gap-4 p-2 bg-white rounded-xl border border-gray-200">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
          <ClipboardList className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-xs text-gray-500">Total Items</div>
        </div>
      </div>

      <div className="w-px bg-gray-200" />

      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
          <Calendar className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{stats.today}</div>
          <div className="text-xs text-gray-500">Today</div>
        </div>
      </div>

      {stats.overdue > 0 && (
        <>
          <div className="w-px bg-gray-200" />
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
              <div className="text-xs text-gray-500">Overdue</div>
            </div>
          </div>
        </>
      )}

      <div className="w-px bg-gray-200" />

      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
          <CalendarCheck className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{stats.events}</div>
          <div className="text-xs text-gray-500">Events</div>
        </div>
      </div>

      <div className="w-px bg-gray-200" />

      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
          <Briefcase className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{stats.bookings}</div>
          <div className="text-xs text-gray-500">Bookings</div>
        </div>
      </div>

      {stats.withReminders > 0 && (
        <>
          <div className="w-px bg-gray-200" />
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Bell className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.withReminders}</div>
              <div className="text-xs text-gray-500">+Reminders</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// --- MAIN COMPONENT ---
export const DiaryPlannerView = ({
  bookId,
  items = [],
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  diaryService,
  customStyling={}
}) => {
  const [filterType, setFilterType] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('UPCOMING'); // UPCOMING, TODAY, WEEK, MONTH, ALL
  const [showCompleted, setShowCompleted] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [creatingType, setCreatingType] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Filter items
  const filteredItems = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const weekFromNow = new Date();
    weekFromNow.setDate(weekFromNow.getDate() + 7);
    const weekStr = weekFromNow.toISOString().split('T')[0];
    const monthFromNow = new Date();
    monthFromNow.setDate(monthFromNow.getDate() + 30);
    const monthStr = monthFromNow.toISOString().split('T')[0];

    return items.filter(item => {
      // Type filter
      if (filterType !== 'ALL' && item.type !== filterType) return false;

      // Status filter
      if (filterStatus !== 'ALL') {
        if (filterStatus === 'PENDING' && 
            ![TaskStatus.TODO, TaskStatus.IN_PROGRESS, BookingStatus.PENDING].includes(item.status)) {
          return false;
        }
        if (filterStatus === 'COMPLETED' && 
            ![TaskStatus.COMPLETED, BookingStatus.COMPLETED, EventStatus.COMPLETED].includes(item.status)) {
          return false;
        }
      }

      // Hide completed unless toggled
      if (!showCompleted && 
          [TaskStatus.COMPLETED, BookingStatus.COMPLETED, EventStatus.COMPLETED].includes(item.status)) {
        return false;
      }

      // Date range filter
      if (dateRange === 'TODAY' && item.date !== today) return false;
      if (dateRange === 'WEEK' && item.date > weekStr) return false;
      if (dateRange === 'MONTH' && item.date > monthStr) return false;
      if (dateRange === 'UPCOMING' && item.date < today) return false;

      // Search
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!item.title?.toLowerCase().includes(q) &&
            !item.description?.toLowerCase().includes(q) &&
            !item.location?.toLowerCase().includes(q) &&
            !item.customerName?.toLowerCase().includes(q) &&
            !item.providerName?.toLowerCase().includes(q)) {
          return false;
        }
      }

      return true;
    }).sort((a, b) => {
      // Sort by date, then time
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return (a.startTime || '00:00').localeCompare(b.startTime || '00:00');
    });
  }, [items, filterType, filterStatus, dateRange, searchQuery, showCompleted]);

  console.log('Planner items', items);

  const handleSave = async (item) => {
    if (editingItem) {
      onUpdateItem?.(item);
    } else {
      onAddItem?.(item);
    }
    setEditingItem(null);
    setCreatingType(null);
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('Delete this item?')) {
      onDeleteItem?.(itemId);
    }
  };

  const handleToggleComplete = (item) => {
    const newStatus = item.status === TaskStatus.COMPLETED ? TaskStatus.TODO : TaskStatus.COMPLETED;
    onUpdateItem?.({ ...item, status: newStatus });
  };

  const typeFilters = [
    { key: 'ALL', label: 'All', icon: ClipboardList },
    { key: ItemType.TASK, label: 'Tasks', icon: ListTodo },
    { key: ItemType.EVENT_HOSTING, label: 'Hosting', icon: CalendarCheck },
    { key: ItemType.EVENT_ATTENDING, label: 'Attending', icon: Ticket },
    { key: ItemType.GENERAL, label: 'General', icon: ClipboardList },
    { key: ItemType.BOOKING_PROVIDER, label: 'Client Bookings', icon: Briefcase },
    { key: ItemType.BOOKING_CUSTOMER, label: 'My Appointments', icon: CalendarClock },
  ];

  return (
    <div className="flex-1 overflow-y-auto flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <CalendarDays className="w-6 h-6 mr-2 text-indigo-600" />
              Planner
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage tasks, events, and bookings
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg border transition-colors ${
                showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-600' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
            >
              <SlidersHorizontal className="w-5 h-5" />
            </button>
            <QuickAddMenu onAdd={(type) => setCreatingType(type)} customStyling={customStyling}/>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="px-3 py-4 shrink-0">
        <StatsBar items={items} />
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="px-3 pb-4 shrink-0">
          <Card className="p-2">
            <div className="flex flex-wrap gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Date Range */}
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
              >
                <option value="TODAY">Today</option>
                <option value="WEEK">Next 7 Days</option>
                <option value="MONTH">Next 30 Days</option>
                <option value="UPCOMING">All Upcoming</option>
                <option value="ALL">All Time</option>
              </select>

              {/* Show Completed */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600"
                />
                <span className="text-sm text-gray-600">Show completed</span>
              </label>
            </div>
          </Card>
        </div>
      )}

      {/* Type Tabs */}
      <div className="px-3 pb-4 shrink-0">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {typeFilters.map(filter => {
            const count = filter.key === 'ALL' 
              ? items.length 
              : items.filter(i => i.type === filter.key).length;
            
            return (
              <button
                key={filter.key}
                onClick={() => setFilterType(filter.key)}
                style={{
                  backgroundColor: filterType === filter.key 
                    ? customStyling?.primaryColor 
                    : undefined
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  filterType === filter.key
                    ? 'text-white shadow-md'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <filter.icon className="w-4 h-4" />
                {filter.label}
                {count > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    filterType === filter.key ? 'bg-white/20' : 'bg-gray-100'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-3 pb-6">
        <TimelineView
          items={filteredItems}
          onEdit={setEditingItem}
          onDelete={handleDelete}
          onToggleComplete={handleToggleComplete}
          onViewDetails={(item) => setEditingItem(item)}
        />
      </div>

      {/* Item Form Modal */}
      <ItemFormModal
        isOpen={!!editingItem || !!creatingType}
        onClose={() => { setEditingItem(null); setCreatingType(null); }}
        onSave={handleSave}
        item={editingItem}
        type={creatingType}
      />
    </div>
  );
};

export default DiaryPlannerView;